export const GAME_STORAGE_VERSION = 1 as const

export type GameMode = 'daily' | 'practice'

export type SavedGameState = {
  version: typeof GAME_STORAGE_VERSION
  puzzleId: string
  solutionIndex: number
  solutionSignature: string
  mode: GameMode
  board: string[][]
  solvedRows: boolean[]
  clueCells: string[]
  revealedCells: string[]
  attempts: number[]
  lastAttempts: (string | null)[]
  mistakes: number
  hintsUsed: number
  startedAt: string
  updatedAt: string
  completedAt: string | null
  statsRecorded: boolean
}

export type PracticePuzzleDescriptor = {
  puzzleId: string
  solutionIndex: number
}

export type StorageLike = {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

type VersionedPracticePuzzleDescriptor = PracticePuzzleDescriptor & {
  version: typeof GAME_STORAGE_VERSION
}

type VersionedPreferredMode = {
  version: typeof GAME_STORAGE_VERSION
  mode: GameMode
}

const STORAGE_PREFIX = 'word-square-puzzle'
const GAME_KEY_PREFIX = `${STORAGE_PREFIX}:game:`
const CURRENT_PRACTICE_KEY = `${STORAGE_PREFIX}:current-practice`
const PREFERRED_MODE_KEY = `${STORAGE_PREFIX}:preferred-mode`
const BOARD_SIZE = 5
const MAX_PUZZLE_ID_LENGTH = 200
const MAX_CELL_ID_LENGTH = 100

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isStorageLike = (value: unknown): value is StorageLike => {
  try {
    return (
      isRecord(value) &&
      typeof value.getItem === 'function' &&
      typeof value.setItem === 'function' &&
      typeof value.removeItem === 'function'
    )
  } catch (_error) {
    return false
  }
}

/**
 * Returns an explicitly supplied storage object, or the browser's localStorage
 * when it can be accessed. Passing null explicitly disables persistence.
 */
export const resolveStorage = (
  storage?: StorageLike | null
): StorageLike | null => {
  if (storage === null) {
    return null
  }
  if (storage !== undefined) {
    return isStorageLike(storage) ? storage : null
  }

  try {
    if (typeof globalThis === 'undefined') {
      return null
    }
    return isStorageLike(globalThis.localStorage)
      ? globalThis.localStorage
      : null
  } catch (_error) {
    // Browsers can expose localStorage while denying access to it.
    return null
  }
}

const isPuzzleId = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= MAX_PUZZLE_ID_LENGTH &&
  value.trim() === value

const isSolutionSignature = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Z]{5}(\|[A-Z]{5}){4}$/.test(value)

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0

const isMode = (value: unknown): value is GameMode =>
  value === 'daily' || value === 'practice'

const isBoardCell = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Z]?$/.test(value)

const isBoard = (value: unknown): value is string[][] =>
  Array.isArray(value) &&
  value.length === BOARD_SIZE &&
  value.every(
    (row) =>
      Array.isArray(row) && row.length === BOARD_SIZE && row.every(isBoardCell)
  )

const isBooleanRows = (value: unknown): value is boolean[] =>
  Array.isArray(value) &&
  value.length === BOARD_SIZE &&
  value.every((entry) => typeof entry === 'boolean')

const isCellIdList = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.length <= BOARD_SIZE * BOARD_SIZE &&
  value.every(
    (entry) =>
      typeof entry === 'string' &&
      entry.length > 0 &&
      entry.length <= MAX_CELL_ID_LENGTH &&
      /^[0-4]:[0-4]$/.test(entry)
  ) &&
  new Set(value).size === value.length

const isAttempts = (value: unknown): value is number[] =>
  Array.isArray(value) &&
  value.length === BOARD_SIZE &&
  value.every(isNonNegativeInteger)

const isLastAttempts = (value: unknown): value is (string | null)[] =>
  Array.isArray(value) &&
  value.length === BOARD_SIZE &&
  value.every((entry) => entry === null || /^[A-Z]{5}$/.test(entry))

const isIsoTimestamp = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}T/.test(value) &&
  Number.isFinite(Date.parse(value))

const hasValidTimestamps = (value: Record<string, unknown>) => {
  if (!isIsoTimestamp(value.startedAt) || !isIsoTimestamp(value.updatedAt)) {
    return false
  }
  if (value.completedAt !== null && !isIsoTimestamp(value.completedAt)) {
    return false
  }

  const startedAt = Date.parse(value.startedAt)
  const updatedAt = Date.parse(value.updatedAt)
  const completedAt =
    value.completedAt === null ? null : Date.parse(value.completedAt)

  return (
    startedAt <= updatedAt &&
    (completedAt === null ||
      (completedAt >= startedAt && completedAt <= updatedAt))
  )
}

const isSavedGameState = (value: unknown): value is SavedGameState =>
  isRecord(value) &&
  value.version === GAME_STORAGE_VERSION &&
  isPuzzleId(value.puzzleId) &&
  isNonNegativeInteger(value.solutionIndex) &&
  isSolutionSignature(value.solutionSignature) &&
  isMode(value.mode) &&
  isBoard(value.board) &&
  isBooleanRows(value.solvedRows) &&
  isCellIdList(value.clueCells) &&
  isCellIdList(value.revealedCells) &&
  isAttempts(value.attempts) &&
  isLastAttempts(value.lastAttempts) &&
  isNonNegativeInteger(value.mistakes) &&
  isNonNegativeInteger(value.hintsUsed) &&
  hasValidTimestamps(value) &&
  typeof value.statsRecorded === 'boolean' &&
  ((value.completedAt === null &&
    !(value.solvedRows as boolean[]).every(Boolean)) ||
    (value.completedAt !== null &&
      (value.solvedRows as boolean[]).every(Boolean))) &&
  (!value.statsRecorded || value.completedAt !== null)

const isPracticePuzzleDescriptor = (
  value: unknown
): value is VersionedPracticePuzzleDescriptor =>
  isRecord(value) &&
  value.version === GAME_STORAGE_VERSION &&
  isPuzzleId(value.puzzleId) &&
  isNonNegativeInteger(value.solutionIndex)

const isPreferredMode = (value: unknown): value is VersionedPreferredMode =>
  isRecord(value) &&
  value.version === GAME_STORAGE_VERSION &&
  isMode(value.mode)

const getGameKey = (puzzleId: string) =>
  `${GAME_KEY_PREFIX}${encodeURIComponent(puzzleId)}`

const parseStoredValue = (value: string | null): unknown => {
  if (value === null) {
    return null
  }
  try {
    return JSON.parse(value) as unknown
  } catch (_error) {
    return null
  }
}

const read = (key: string, storage?: StorageLike | null): unknown => {
  const target = resolveStorage(storage)
  if (!target) {
    return null
  }
  try {
    return parseStoredValue(target.getItem(key))
  } catch (_error) {
    return null
  }
}

const write = (
  key: string,
  value: unknown,
  storage?: StorageLike | null
): boolean => {
  const target = resolveStorage(storage)
  if (!target) {
    return false
  }
  try {
    const serialized = JSON.stringify(value)
    if (serialized === undefined) {
      return false
    }
    target.setItem(key, serialized)
    return true
  } catch (_error) {
    return false
  }
}

const remove = (key: string, storage?: StorageLike | null): boolean => {
  const target = resolveStorage(storage)
  if (!target) {
    return false
  }
  try {
    target.removeItem(key)
    return true
  } catch (_error) {
    return false
  }
}

export const saveGameState = (
  gameState: SavedGameState,
  storage?: StorageLike | null
): boolean => {
  if (!isSavedGameState(gameState)) {
    return false
  }
  return write(getGameKey(gameState.puzzleId), gameState, storage)
}

export const loadGameState = (
  puzzleId: string,
  storage?: StorageLike | null
): SavedGameState | null => {
  if (!isPuzzleId(puzzleId)) {
    return null
  }
  const value = read(getGameKey(puzzleId), storage)
  return isSavedGameState(value) && value.puzzleId === puzzleId ? value : null
}

export const clearGameState = (
  puzzleId: string,
  storage?: StorageLike | null
): boolean =>
  isPuzzleId(puzzleId) ? remove(getGameKey(puzzleId), storage) : false

export const saveCurrentPractice = (
  descriptor: PracticePuzzleDescriptor,
  storage?: StorageLike | null
): boolean => {
  const value: VersionedPracticePuzzleDescriptor = {
    ...descriptor,
    version: GAME_STORAGE_VERSION,
  }
  return isPracticePuzzleDescriptor(value)
    ? write(CURRENT_PRACTICE_KEY, value, storage)
    : false
}

export const loadCurrentPractice = (
  storage?: StorageLike | null
): PracticePuzzleDescriptor | null => {
  const value = read(CURRENT_PRACTICE_KEY, storage)
  return isPracticePuzzleDescriptor(value)
    ? { puzzleId: value.puzzleId, solutionIndex: value.solutionIndex }
    : null
}

export const clearCurrentPractice = (storage?: StorageLike | null): boolean =>
  remove(CURRENT_PRACTICE_KEY, storage)

export const savePreferredMode = (
  mode: GameMode,
  storage?: StorageLike | null
): boolean =>
  isMode(mode)
    ? write(
        PREFERRED_MODE_KEY,
        { version: GAME_STORAGE_VERSION, mode },
        storage
      )
    : false

export const loadPreferredMode = (
  storage?: StorageLike | null
): GameMode | null => {
  const value = read(PREFERRED_MODE_KEY, storage)
  return isPreferredMode(value) ? value.mode : null
}
